/**
 * Online sign-up of a club (0024): the form's fields, their rules and the
 * numbers around it. One implementation for the API (which decides) and the
 * web form (which says the same thing before sending) — a rule only the form
 * knows is no rule (CLAUDE.md rule 1), and a rule only the API knows is a form
 * that fails after five steps.
 *
 * Messages say „du“ like the rest of the app.
 */

import { isValidEmail, normalizeEmail } from './validation';
import type { DeclaredFleet, PaymentInterval } from './pricing';
import { TENANT_NAME_MAX } from './tenant';

/** Countries a club may be registered in. */
export const SIGNUP_COUNTRIES = ['DE', 'AT', 'CH'] as const;
export type SignupCountry = (typeof SIGNUP_COUNTRIES)[number];
export const SIGNUP_COUNTRY_NAMES: Readonly<Record<SignupCountry, string>> = {
  DE: 'Deutschland',
  AT: 'Österreich',
  CH: 'Schweiz',
};

/**
 * The confirmation code of a sign-up is valid this long. Longer than a
 * sign-in code: the mail goes to an address nobody has used with us yet, and
 * some providers hold a first mail back for an hour (web.de, October 2026).
 */
export const SIGNUP_CODE_TTL_MINUTES = 60;
/** An unconfirmed sign-up expires after this; the club starts again. */
export const SIGNUP_PENDING_HOURS = 48;
/**
 * The form must have been open this long before it is sent. The API checks
 * it on a token it signed itself (GET public/signup-info), not on a clock the
 * browser reports.
 */
export const SIGNUP_MIN_FILL_SECONDS = 8;
/** A form token older than this is refused (reload the page). */
export const SIGNUP_FORM_TOKEN_HOURS = 24;
/** Sign-ups per caller address and hour, and per contact address and day. */
export const SIGNUPS_PER_IP_PER_HOUR = 10;
export const SIGNUPS_PER_ADDRESS_PER_DAY = 3;
/** Code entries per caller address and hour, across sign-ups. */
export const SIGNUP_CONFIRMS_PER_IP_PER_HOUR = 30;
/** Aircraft per class on the form. */
export const SIGNUP_FLEET_MAX = 999;

export const SIGNUP_LIMITS = {
  clubName: TENANT_NAME_MAX,
  street: 200,
  city: 120,
  registerCourt: 120,
  registerNumber: 40,
  representativeName: 120,
  representativeRole: 80,
  phone: 40,
  billingName: 200,
  vatId: 20,
} as const;

/** The body of POST /api/signup. Optional texts may be empty strings. */
export interface SignupInput {
  clubName: string;
  street: string;
  postalCode: string;
  city: string;
  country: string;
  registerCourt?: string;
  registerNumber?: string;
  representativeName: string;
  representativeRole: string;
  contactEmail: string;
  contactPhone?: string;
  billingName?: string;
  billingStreet?: string;
  billingPostalCode?: string;
  billingCity?: string;
  billingEmail?: string;
  vatId?: string;
  paymentInterval: string;
  fleet: { motor?: unknown; tmg?: unknown; glider?: unknown; ul?: unknown };
  acceptAgb: boolean;
  acceptAvv: boolean;
  confirmAuthority: boolean;
}

/** What is stored after the checks: trimmed, normalised, optional fields null. */
export interface SignupData {
  readonly clubName: string;
  readonly street: string;
  readonly postalCode: string;
  readonly city: string;
  readonly country: SignupCountry;
  readonly registerCourt: string | null;
  readonly registerNumber: string | null;
  readonly representativeName: string;
  readonly representativeRole: string;
  readonly contactEmail: string;
  readonly contactPhone: string | null;
  readonly billingName: string | null;
  readonly billingStreet: string | null;
  readonly billingPostalCode: string | null;
  readonly billingCity: string | null;
  readonly billingEmail: string | null;
  readonly vatId: string | null;
  readonly paymentInterval: PaymentInterval;
  readonly fleet: DeclaredFleet;
}

export type SignupField =
  | Exclude<keyof SignupInput, 'fleet'>
  | 'fleet'
  | 'fleet.motor'
  | 'fleet.tmg'
  | 'fleet.glider'
  | 'fleet.ul'
  | 'billing';

export type SignupErrors = Partial<Record<SignupField, string>>;

const text = (v: unknown): string => (typeof v === 'string' ? v.trim().replace(/\s+/g, ' ') : '');
const opt = (v: unknown): string | null => text(v) || null;

const POSTAL: Readonly<Record<SignupCountry, RegExp>> = {
  DE: /^\d{5}$/,
  AT: /^\d{4}$/,
  CH: /^\d{4}$/,
};

export function isSignupCountry(v: unknown): v is SignupCountry {
  return typeof v === 'string' && (SIGNUP_COUNTRIES as readonly string[]).includes(v);
}

function fleetCount(v: unknown): number | null {
  if (v === undefined || v === null || v === '') return 0;
  const n = typeof v === 'number' ? v : typeof v === 'string' && /^\d+$/.test(v.trim()) ? Number(v.trim()) : NaN;
  return Number.isInteger(n) && n >= 0 && n <= SIGNUP_FLEET_MAX ? n : null;
}

/** USt-IdNr. as stored: upper case, no spaces, country prefix and 2–13 letters/digits (EU format). */
export function normalizeVatId(v: unknown): string {
  return text(v).replace(/[\s.-]/g, '').toUpperCase();
}

/**
 * Checks the whole form. Steps of the web form check their own fields with
 * the same function (`fieldsOfStep`), so a message appears where the field is.
 */
export function validateSignup(input: Partial<SignupInput> | null | undefined): { ok: true; value: SignupData } | { ok: false; errors: SignupErrors } {
  const i = (input ?? {}) as Partial<SignupInput>;
  const e: SignupErrors = {};
  const L = SIGNUP_LIMITS;

  const clubName = text(i.clubName);
  if (clubName.length < 3) e.clubName = 'Bitte gib den Namen deines Vereins laut Vereinsregister ein.';
  else if (clubName.length > L.clubName) e.clubName = `Höchstens ${L.clubName} Zeichen.`;

  const street = text(i.street);
  if (street.length < 3) e.street = 'Bitte gib Straße und Hausnummer ein.';
  else if (street.length > L.street) e.street = `Höchstens ${L.street} Zeichen.`;

  const country = isSignupCountry(i.country) ? i.country : null;
  if (!country) e.country = 'Bitte wähle Deutschland, Österreich oder die Schweiz.';

  const postalCode = text(i.postalCode);
  if (!postalCode) e.postalCode = 'Bitte gib die Postleitzahl ein.';
  else if (country && !POSTAL[country].test(postalCode))
    e.postalCode = country === 'DE' ? 'Eine deutsche Postleitzahl hat fünf Ziffern.' : 'Die Postleitzahl hat vier Ziffern.';

  const city = text(i.city);
  if (city.length < 2) e.city = 'Bitte gib den Ort ein.';
  else if (city.length > L.city) e.city = `Höchstens ${L.city} Zeichen.`;

  const registerCourt = opt(i.registerCourt);
  const registerNumber = opt(i.registerNumber);
  if (registerCourt && registerCourt.length > L.registerCourt) e.registerCourt = `Höchstens ${L.registerCourt} Zeichen.`;
  if (registerNumber && registerNumber.length > L.registerNumber) e.registerNumber = `Höchstens ${L.registerNumber} Zeichen.`;
  if (registerCourt && !registerNumber && !e.registerCourt) e.registerNumber = 'Bitte gib auch die Registernummer ein (z. B. VR 1234).';
  if (registerNumber && !registerCourt && !e.registerNumber) e.registerCourt = 'Bitte gib auch das Registergericht ein.';

  const representativeName = text(i.representativeName);
  if (representativeName.length < 3) e.representativeName = 'Bitte gib deinen vollständigen Namen ein.';
  else if (representativeName.length > L.representativeName) e.representativeName = `Höchstens ${L.representativeName} Zeichen.`;

  const representativeRole = text(i.representativeRole);
  if (representativeRole.length < 2) e.representativeRole = 'Bitte gib deine Funktion im Verein ein, z. B. 1. Vorsitzender.';
  else if (representativeRole.length > L.representativeRole) e.representativeRole = `Höchstens ${L.representativeRole} Zeichen.`;

  const contactEmail = normalizeEmail(typeof i.contactEmail === 'string' ? i.contactEmail : '');
  if (!isValidEmail(contactEmail)) e.contactEmail = 'Bitte gib eine gültige E-Mail-Adresse ein.';

  const contactPhone = opt(i.contactPhone);
  if (contactPhone && (contactPhone.length > L.phone || !/^\+?[0-9 ()/-]{4,}$/.test(contactPhone)))
    e.contactPhone = 'Bitte gib eine Telefonnummer aus Ziffern ein, gern mit +49.';

  const billingName = opt(i.billingName);
  const billingStreet = opt(i.billingStreet);
  const billingPostalCode = opt(i.billingPostalCode);
  const billingCity = opt(i.billingCity);
  const anyBilling = billingName || billingStreet || billingPostalCode || billingCity;
  if (anyBilling) {
    if (!billingName) e.billingName = 'Bitte gib den Namen für die Rechnung ein.';
    else if (billingName.length > L.billingName) e.billingName = `Höchstens ${L.billingName} Zeichen.`;
    if (!billingStreet) e.billingStreet = 'Bitte gib Straße und Hausnummer ein.';
    else if (billingStreet.length > L.street) e.billingStreet = `Höchstens ${L.street} Zeichen.`;
    if (!billingPostalCode) e.billingPostalCode = 'Bitte gib die Postleitzahl ein.';
    else if (country && !POSTAL[country].test(billingPostalCode)) e.billingPostalCode = 'Bitte prüfe die Postleitzahl.';
    if (!billingCity) e.billingCity = 'Bitte gib den Ort ein.';
    else if (billingCity.length > L.city) e.billingCity = `Höchstens ${L.city} Zeichen.`;
  }

  const billingEmailRaw = opt(i.billingEmail);
  const billingEmail = billingEmailRaw ? normalizeEmail(billingEmailRaw) : null;
  if (billingEmail && !isValidEmail(billingEmail)) e.billingEmail = 'Bitte gib eine gültige E-Mail-Adresse ein.';

  const vat = normalizeVatId(i.vatId);
  const vatId = vat || null;
  if (vatId && (vatId.length > L.vatId || !/^[A-Z]{2}[A-Z0-9]{2,13}$/.test(vatId)))
    e.vatId = 'Bitte prüfe die USt-IdNr. (z. B. DE123456789).';

  const paymentInterval = i.paymentInterval === 'monthly' || i.paymentInterval === 'yearly' ? i.paymentInterval : null;
  if (!paymentInterval) e.paymentInterval = 'Bitte wähle, ob ihr monatlich oder jährlich zahlt.';

  const f = (i.fleet ?? {}) as SignupInput['fleet'];
  const motor = fleetCount(f.motor);
  const tmg = fleetCount(f.tmg);
  const glider = fleetCount(f.glider);
  const ul = fleetCount(f.ul);
  const bad = `Bitte eine Zahl von 0 bis ${SIGNUP_FLEET_MAX}.`;
  if (motor === null) e['fleet.motor'] = bad;
  if (tmg === null) e['fleet.tmg'] = bad;
  if (glider === null) e['fleet.glider'] = bad;
  if (ul === null) e['fleet.ul'] = bad;
  if (motor !== null && tmg !== null && glider !== null && ul !== null && motor + tmg + glider + ul === 0)
    e.fleet = 'Bitte gib mindestens ein vereinseigenes Flugzeug an.';

  if (i.confirmAuthority !== true) e.confirmAuthority = 'Bitte bestätige, dass du den Verein vertreten darfst.';
  if (i.acceptAgb !== true) e.acceptAgb = 'Bitte stimme den AGB zu.';
  if (i.acceptAvv !== true) e.acceptAvv = 'Bitte stimme dem Vertrag zur Auftragsverarbeitung zu.';

  if (Object.keys(e).length > 0) return { ok: false, errors: e };
  return {
    ok: true,
    value: {
      clubName,
      street,
      postalCode,
      city,
      country: country!,
      registerCourt,
      registerNumber,
      representativeName,
      representativeRole,
      contactEmail,
      contactPhone,
      billingName,
      billingStreet,
      billingPostalCode,
      billingCity,
      billingEmail: billingEmail && billingEmail !== contactEmail ? billingEmail : null,
      vatId,
      paymentInterval: paymentInterval!,
      fleet: { motor: motor!, tmg: tmg!, glider: glider!, ul: ul! },
    },
  };
}

/** Fields per step of the web form, so a step shows only its own messages. */
export const SIGNUP_STEP_FIELDS: readonly (readonly SignupField[])[] = [
  ['clubName', 'street', 'postalCode', 'city', 'country', 'registerCourt', 'registerNumber'],
  ['representativeName', 'representativeRole', 'contactEmail', 'contactPhone', 'confirmAuthority'],
  ['fleet', 'fleet.motor', 'fleet.tmg', 'fleet.glider', 'fleet.ul', 'paymentInterval'],
  ['billingName', 'billingStreet', 'billingPostalCode', 'billingCity', 'billingEmail', 'vatId'],
  ['acceptAgb', 'acceptAvv'],
];

/** The errors of one step (0-based), from a whole-form check. */
export function errorsOfStep(errors: SignupErrors, step: number): SignupErrors {
  const fields = SIGNUP_STEP_FIELDS[step] ?? [];
  return Object.fromEntries(Object.entries(errors).filter(([k]) => (fields as readonly string[]).includes(k))) as SignupErrors;
}

/** „AC-2026-0001“: the year the contract was concluded and its running number in that year. */
export function formatContractNumber(year: number, n: number): string {
  return `AC-${year}-${String(n).padStart(4, '0')}`;
}
